import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, CheckCircle2, Send } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  parent_name: "",
  email: "",
  phone: "",
  children_count: "1",
  children_ages: "",
  address: "",
  preferred_date: "",
  preferred_time: "",
  duration: "",
  message: "",
  unsure_time: false,
};

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3.5 text-[0.95rem] text-ink placeholder:text-mutedink/50 transition-colors duration-200 focus:border-petrol/40";

const ErrorText = ({ children }) => (
  <p className="mt-1.5 text-xs font-medium text-[#B4544C]">{children}</p>
);

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.parent_name.trim().length < 2) errs.parent_name = "Kerrothan nimesi";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Tarkista sähköpostiosoite";
    if (form.phone.trim().length < 6) errs.phone = "Tarkista puhelinnumero";
    if (!form.unsure_time && !form.preferred_date) errs.preferred_date = "Valitse päivä tai merkitse, ettet ole vielä varma ajankohdasta";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSending(true);
    try {
      await new Promise((r) => setTimeout(r, 600));
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, parent_name: form.parent_name.trim(), email: form.email.trim(), phone: form.phone.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
      toast.success("Viesti lähetetty – kiitos! Vastaan sinulle mahdollisimman pian.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Viestin lähetys ei onnistunut. Yritä hetken päästä uudelleen.");
    } finally {
      setSending(false);
    }
  };

  const unsure = form.unsure_time;

  return (
    <section id="yhteydenotto" className="bg-greige/60 py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-5 lg:gap-16">
        <div className="lg:col-span-2">
          <Reveal>
            <Eyebrow index="06" label="Yhteys" />
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-4xl font-medium tracking-tight text-petrol-night sm:text-5xl">
              Odotan innolla yhteydenottoasi
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-mutedink sm:text-lg">
              Kerro toiveesi, niin vastaan sinulle mahdollisimman pian. Tarkkaa
              ajankohtaa ei tarvitse vielä tietää – voit lähettää myös
              vapaamuotoisen kysymyksen.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <ul className="mt-10 space-y-5">
              <li className="flex items-center gap-4" data-testid="contact-info-email">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-cream text-petrol">
                  <Mail className="h-4 w-4" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-mutedink">Sähköposti</p>
                  <p className="text-[0.95rem] font-semibold text-petrol-night">kaisa.aleksandra.kivinen@gmail.com</p>
                </div>
              </li>
              <li className="flex items-center gap-4" data-testid="contact-info-area">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-cream text-petrol">
                  <MapPin className="h-4 w-4" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-mutedink">Palvelualue</p>
                  <p className="text-[0.95rem] font-semibold text-petrol-night">Helsinki · Espoo · Vantaa</p>
                </div>
              </li>
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-3">
          <div className="rounded-[2rem] border border-line bg-cream p-7 shadow-[0_40px_80px_-50px_rgba(18,58,62,0.4)] sm:p-10">
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  data-testid="contact-form-success-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex min-h-[30rem] flex-col items-center justify-center text-center"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-petrol-soft text-petrol">
                    <CheckCircle2 className="h-8 w-8" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-7 font-serif text-3xl font-semibold text-petrol-night">Kiitos viestistäsi!</h3>
                  <p className="mt-4 max-w-sm text-base leading-relaxed text-mutedink">
                    Luen jokaisen viestin itse ja vastaan sinulle
                    mahdollisimman pian.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-8 rounded-full border border-petrol/25 px-7 py-3 text-sm font-semibold text-petrol transition-all hover:bg-petrol hover:text-cream"
                    data-testid="contact-form-new-message-button"
                  >
                    Lähetä uusi viesti
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -16 }}
                  onSubmit={submit}
                  noValidate
                  data-testid="contact-form"
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="cf-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                        Vanhemman nimi *
                      </label>
                      <input id="cf-name" data-testid="contact-form-name-input" className={inputCls} value={form.parent_name} onChange={set("parent_name")} placeholder="Matti Meikäläinen" />
                      {errors.parent_name && <ErrorText>{errors.parent_name}</ErrorText>}
                    </div>
                    <div>
                      <label htmlFor="cf-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                        Sähköposti *
                      </label>
                      <input id="cf-email" data-testid="contact-form-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="matti@esimerkki.fi" />
                      {errors.email && <ErrorText>{errors.email}</ErrorText>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="cf-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                        Puhelinnumero *
                      </label>
                      <input id="cf-phone" data-testid="contact-form-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+358 40 123 4567" />
                      {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
                    </div>
                    <div>
                      <label htmlFor="cf-count" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                        Lasten määrä
                      </label>
                      <select id="cf-count" data-testid="contact-form-children-input" className={inputCls} value={form.children_count} onChange={set("children_count")}>
                        {["1", "2", "3", "4+"].map((n) => (
                          <option key={n} value={n}>{n} {n === "1" ? "lapsi" : "lasta"}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="cf-ages" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                      Lasten iät
                    </label>
                    <input id="cf-ages" data-testid="contact-form-ages-input" className={inputCls} value={form.children_ages} onChange={set("children_ages")} placeholder="esim. 2 v. ja 5 v." />
                  </div>

                  <div>
                    <label htmlFor="cf-address" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                      Missä asutte?
                    </label>
                    <input id="cf-address" data-testid="contact-form-address-input" className={inputCls} value={form.address} onChange={set("address")} placeholder="esim. Töölö, Helsinki" />
                  </div>

                  <div className={`grid grid-cols-1 gap-5 transition-opacity duration-300 sm:grid-cols-3 ${unsure ? "opacity-40" : ""}`}>
                    <div>
                      <label htmlFor="cf-date" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                        Toivottu päivämäärä {!unsure && "*"}
                      </label>
                      <input id="cf-date" data-testid="contact-form-date-input" type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} disabled={unsure} />
                      {!unsure && errors.preferred_date && <ErrorText>{errors.preferred_date}</ErrorText>}
                    </div>
                    <div>
                      <label htmlFor="cf-time" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                        Kellonaika
                      </label>
                      <input id="cf-time" data-testid="contact-form-time-input" type="time" className={inputCls} value={form.preferred_time} onChange={set("preferred_time")} disabled={unsure} />
                    </div>
                    <div>
                      <label htmlFor="cf-duration" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                        Arvioitu kesto
                      </label>
                      <select id="cf-duration" data-testid="contact-form-duration-input" className={inputCls} value={form.duration} onChange={set("duration")} disabled={unsure}>
                        <option value="">Valitse…</option>
                        <option value="3 h">3 tuntia</option>
                        <option value="4 h">4 tuntia</option>
                        <option value="5-6 h">5–6 tuntia</option>
                        <option value="Koko ilta">Koko ilta</option>
                        <option value="Yöhoito">Yöhoito</option>
                      </select>
                    </div>
                  </div>

                  <label
                    data-testid="contact-form-inquiry-checkbox-label"
                    className="flex cursor-pointer items-start gap-3.5 rounded-xl border border-beige-border/60 bg-beige/25 px-5 py-4 transition-colors hover:bg-beige/40"
                  >
                    <input
                      type="checkbox"
                      data-testid="contact-form-inquiry-checkbox"
                      checked={unsure}
                      onChange={set("unsure_time")}
                      className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer appearance-none rounded-md border border-petrol/40 bg-white transition-colors checked:border-petrol checked:bg-petrol relative after:absolute after:left-1.5 after:top-0.5 after:h-3 after:w-2 after:border-r-2 after:border-b-2 after:border-cream after:rotate-45 after:content-[''] checked:after:block"
                    />
                    <span className="text-sm leading-snug text-ink/85">
                      En ole vielä varma ajankohdasta – haluan vain kysyä
                      lastenhoidosta.
                    </span>
                  </label>

                  <div>
                    <label htmlFor="cf-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-mutedink">
                      Viesti / lisätiedot
                    </label>
                    <textarea id="cf-message" data-testid="contact-form-message-input" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Kerro esimerkiksi iltarutiineista, lempileikeistä tai muista tärkeistä asioista." />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={sending}
                    className="group flex w-full items-center justify-center gap-3 rounded-full bg-petrol py-4 text-base font-semibold text-cream shadow-[0_18px_40px_-16px_rgba(18,58,62,0.65)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-petrol-dark disabled:cursor-wait disabled:opacity-70"
                  >
                    {sending ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-cream/30 border-t-cream" />
                        Lähetetään…
                      </>
                    ) : (
                      <>
                        Lähetä viesti
                        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-mutedink">
                    Tietosi käsitellään luottamuksellisesti ja vain
                    lastenhoidon järjestämiseksi.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
